import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, CheckCircle2, MessageCircle, Sparkles, Phone, Mail,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { whatsappUrl, mailtoUrl, CONTACT_EMAIL } from "@/lib/channels";
import { PROJECT_TYPES, BUDGET_RANGES, COUNTRIES } from "@/data/leadOptions";

/**
 * Premium lead capture dialog — single source of truth for every "Start Your Project" /
 * "Schedule a Consultation" / "Talk to an Expert" CTA across the site.
 *
 * - Captures all required fields (name, company, email, phone, country, project type,
 *   budget, requirements) plus a honeypot for spam protection.
 * - Posts to POST /api/contact which fires Resend transactional emails to
 *   info@zolvix.in (notification) and the visitor (confirmation).
 * - Success state renders the WhatsApp CTA + confirmation message inline so the user
 *   never has to leave the conversation.
 */
export default function LeadDialog({
  trigger,
  source = "lead_dialog",
  titleVariant = "consultation",
  defaultProjectType,
}) {
  const TITLES = {
    consultation: {
      overline: "30-min Consultation",
      title: "Schedule a Consultation",
      desc: "Share a few details about your project. A senior member of our team will respond within one business day.",
    },
    project: {
      overline: "Start Your Project",
      title: "Tell us what you're building",
      desc: "From discovery to deployment — let's scope what success looks like together.",
    },
    expert: {
      overline: "Talk to an Expert",
      title: "Talk to a Zolvix expert",
      desc: "A short, no-pressure call to understand your context and recommend a path forward.",
    },
    callback: {
      overline: "Request a Callback",
      title: "We'll call you back",
      desc: "Leave your details and a senior member of our team will reach out at a convenient time.",
    },
  };
  const copy = TITLES[titleVariant] || TITLES.consultation;

  const {
    register, handleSubmit, reset, formState: { errors },
  } = useForm({
    defaultValues: { project_type: defaultProjectType || "" },
  });
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(null); // submitted payload on success

  // Reset state every time the dialog re-opens fresh.
  useEffect(() => {
    if (!open) {
      setTimeout(() => { setSuccess(null); reset(); }, 200);
    }
  }, [open, reset]);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const payload = {
        name: data.name,
        company: data.company || "",
        email: data.email,
        phone: data.phone,
        country: data.country,
        project_type: data.project_type,
        budget: data.budget || "",
        project_details: data.project_details,
        source,
        website: data.website || "", // honeypot
      };
      await api.post("/contact", payload);
      setSuccess(payload);
      toast.success("Thanks — your enquiry has been received.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Something went wrong. Please try again or use WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="lead-dialog"
        className="sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0B2138] border border-white/10 text-white p-0"
      >
        <div className="px-6 pt-6 pb-4 border-b border-white/[0.06] sticky top-0 bg-[#0B2138]/95 backdrop-blur z-10">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-9 w-9 rounded-lg grid place-items-center bg-cyan/10 border border-cyan/30 text-cyan">
                <Sparkles className="h-4 w-4" />
              </div>
              <span className="overline">{copy.overline}</span>
            </div>
            <DialogTitle className="font-display text-xl md:text-2xl font-semibold text-white">
              {copy.title}
            </DialogTitle>
            <DialogDescription className="text-ink-400 text-sm">
              {copy.desc}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5">
          {success ? (
            <SuccessState lead={success} onClose={() => setOpen(false)} />
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="lead-form" noValidate>
              {/* Honeypot — hidden from real users; bots will fill it. */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                {...register("website")}
                className="hidden"
                aria-hidden="true"
              />

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" error={errors.name?.message} testid="lead-name" required>
                  <input
                    {...register("name", { required: "Required", maxLength: { value: 120, message: "Too long" } })}
                    className={inputCls} placeholder="Your name"
                  />
                </Field>
                <Field label="Company name" error={errors.company?.message} testid="lead-company" required>
                  <input
                    {...register("company", { required: "Required" })}
                    className={inputCls} placeholder="Company"
                  />
                </Field>
                <Field label="Work email" error={errors.email?.message} testid="lead-email" required>
                  <input
                    type="email"
                    {...register("email", {
                      required: "Required",
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email" },
                    })}
                    className={inputCls} placeholder="you@company.com"
                  />
                </Field>
                <Field label="Phone number" error={errors.phone?.message} testid="lead-phone" required>
                  <input
                    type="tel"
                    {...register("phone", {
                      required: "Required",
                      minLength: { value: 6, message: "Enter a valid phone number" },
                    })}
                    className={inputCls} placeholder="+91 / +971 / +1 …"
                  />
                </Field>
                <Field label="Country" error={errors.country?.message} testid="lead-country" required>
                  <select
                    {...register("country", { required: "Required" })}
                    className={inputCls + " appearance-none"} defaultValue=""
                  >
                    <option value="" disabled>Select your country</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Project type" error={errors.project_type?.message} testid="lead-project-type" required>
                  <select
                    {...register("project_type", { required: "Required" })}
                    className={inputCls + " appearance-none"}
                  >
                    <option value="" disabled>Select a project type</option>
                    {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Estimated budget (optional)" testid="lead-budget" className="sm:col-span-2">
                  <select {...register("budget")} className={inputCls + " appearance-none"} defaultValue="">
                    <option value="">Prefer not to say</option>
                    {BUDGET_RANGES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Project requirements" error={errors.project_details?.message} testid="lead-requirements" required>
                <textarea
                  rows={5}
                  {...register("project_details", {
                    required: "Required",
                    minLength: { value: 10, message: "Please share a few more details (10+ characters)" },
                    maxLength: { value: 4000, message: "Too long — please keep under 4000 characters" },
                  })}
                  className={inputCls}
                  placeholder="What are you building, the outcome you're after, timelines, and any constraints we should know about."
                />
              </Field>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
                <button type="submit" disabled={submitting} className="btn-primary flex-1" data-testid="lead-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send enquiry <ArrowRight className="h-4 w-4" /></>}
                </button>
                <a
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary"
                  data-testid="lead-whatsapp-cta"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp us
                </a>
              </div>

              <p className="text-[11px] text-ink-400">
                By submitting this form you agree we may contact you about your enquiry. We don&apos;t share your details with third parties.
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SuccessState({ lead, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="space-y-5"
      data-testid="lead-success"
    >
      <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/[0.06] p-5">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-300" />
          <div className="font-display text-base font-semibold text-white">Enquiry received</div>
        </div>
        <p className="mt-2 text-sm text-ink-200 leading-relaxed">
          Thank you for contacting <span className="text-white font-semibold">Zolvix Technologies</span>. Our team will review your requirements and get back to you shortly — typically within one business day. A confirmation has been sent to <span className="text-cyan">{lead.email}</span>.
        </p>
      </div>

      <div className="rounded-xl border border-cyan/25 bg-cyan/[0.05] p-5">
        <div className="font-display text-base font-semibold text-white">Need an immediate response?</div>
        <p className="mt-1 text-sm text-ink-200">Chat with us on WhatsApp — we typically reply within minutes during business hours.</p>
        <a
          href={whatsappUrl(`Hi Zolvix Team, I just submitted an enquiry on your website (${lead.project_type || "a project"}). Looking forward to discussing.`)}
          target="_blank"
          rel="noreferrer"
          className="btn-primary mt-4"
          data-testid="lead-success-whatsapp"
        >
          <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
        </a>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <a
          href={mailtoUrl(`Follow-up — ${lead.name}`, "")}
          className="card-surface p-4 flex items-center gap-3 hover:border-cyan/30"
          data-testid="lead-success-email"
        >
          <div className="h-9 w-9 rounded-md grid place-items-center bg-cyan/10 border border-cyan/25 text-cyan">
            <Mail className="h-4 w-4" />
          </div>
          <div>
            <div className="text-white font-semibold">Email us</div>
            <div className="text-xs text-ink-400">{CONTACT_EMAIL}</div>
          </div>
        </a>
        <a
          href="tel:+918828271040"
          className="card-surface p-4 flex items-center gap-3 hover:border-cyan/30"
          data-testid="lead-success-phone"
        >
          <div className="h-9 w-9 rounded-md grid place-items-center bg-cyan/10 border border-cyan/25 text-cyan">
            <Phone className="h-4 w-4" />
          </div>
          <div>
            <div className="text-white font-semibold">Call Mumbai</div>
            <div className="text-xs text-ink-400">+91 88282 71040</div>
          </div>
        </a>
      </div>

      <button onClick={onClose} className="btn-secondary w-full" data-testid="lead-success-close">
        Close
      </button>
    </motion.div>
  );
}

const inputCls =
  "w-full rounded-lg bg-[#0F2B48] border border-white/[0.08] px-3.5 py-2.5 text-sm text-white placeholder:text-ink-400 focus:outline-none focus:border-cyan/50 focus:ring-2 focus:ring-cyan/20 transition";

function Field({ label, error, children, testid, className = "", required }) {
  return (
    <label className={"block " + className} data-testid={testid}>
      <span className="text-xs text-ink-200">
        {label}{required && <span className="text-cyan ml-1">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-[11px] text-red-300">{error}</span>}
    </label>
  );
}
